// src/pages/Documentation/index.tsx
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { DocsSidebar } from './DocsSidebar';
import { DocsContent } from './DocsContent';
import { documentationStructure } from './docMap';
import './Docs.scss';
import { ThemeContext } from '../../ThemeContext';

const Docs = () => {
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const { sectionId } = useParams();
  const [isToggleVisible, setIsToggleVisible] = useState(true);
  const [isToggleHiding, setIsToggleHiding] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [initialExpandedGroups, setInitialExpandedGroups] = useState({});

  // Helper function to find section in nested structure
  const findSectionInStructure = (id) => {
    // First check top-level links
    const topLevelSection = documentationStructure.find(
      (item) => item.type === 'link' && item.id === id
    );
    if (topLevelSection) return topLevelSection;

    // Helper function to recursively search in groups
    const searchInGroup = (group) => {
      if (!group.sections) return null;

      for (const section of group.sections) {
        if (section.type === 'group') {
          // Recursively search in nested groups
          const found = searchInGroup(section);
          if (found) return found;
        } else if (section.id === id) {
          return {
            type: 'link',
            id: section.id,
            title: section.title,
            path: section.path,
          };
        }
      }
      return null;
    };

    // Then check in groups (including nested groups)
    for (const item of documentationStructure) {
      if (item.type === 'group') {
        const found = searchInGroup(item);
        if (found) return found;
      }
    }
    return null;
  };

  // Helper function to find which group(s) contain a section
  const findSectionGroup = (sectionId) => {
    // Helper function to recursively search in groups and collect all parent group IDs
    const searchInGroup = (group, parentGroups = []) => {
      if (!group.sections) return null;

      const currentPath = [...parentGroups, group.id];

      for (const section of group.sections) {
        if (section.type === 'group') {
          // Check if section is in this nested group
          const found = searchInGroup(section, currentPath);
          if (found) return found;
        } else if (section.id === sectionId) {
          return currentPath;
        }
      }
      return null;
    };

    for (const item of documentationStructure) {
      if (item.type === 'group') {
        const found = searchInGroup(item);
        if (found) return found;
      }
    }
    return null;
  };

  // Get default section (first link in structure)
  const getDefaultSection = () => {
    // Helper function to find the first link recursively
    const findFirstLink = (items) => {
      for (const item of items) {
        if (item.type === 'link') {
          return item;
        } else if (item.type === 'group' && item.sections) {
          const found = findFirstLink(item.sections);
          if (found) return found;
        }
      }
      return null;
    };

    return findFirstLink(documentationStructure);
  };

  const [activeSection, setActiveSection] = useState(
    sectionId ? findSectionInStructure(sectionId) : getDefaultSection()
  );

  // Handle URL changes
  useEffect(() => {
    if (sectionId) {
      const section = findSectionInStructure(sectionId);
      if (section) {
        setActiveSection(section);
        // Find and expand all parent groups if section is in nested groups
        const groupPath = findSectionGroup(sectionId);
        if (groupPath && Array.isArray(groupPath)) {
          const expandedGroups = {};
          groupPath.forEach((groupId) => {
            expandedGroups[groupId] = true;
          });
          setInitialExpandedGroups((prev) => ({
            ...prev,
            ...expandedGroups,
          }));
        }
      } else {
        const defaultSection = getDefaultSection();
        if (defaultSection?.id) {
          navigate(`/docs/${defaultSection.id}`);
        }
      }
    } else {
      const defaultSection = getDefaultSection();
      if (defaultSection?.id) {
        navigate(`/docs/${defaultSection.id}`);
      }
    }
  }, [sectionId, navigate]);

  const handleToggleClick = () => {
    setIsToggleHiding(true);
    setTimeout(() => {
      setIsToggleVisible(false);
      setIsSidebarOpen(true);
    }, 300);
  };

  const handleSidebarClose = () => {
    setIsSidebarOpen(false);
    setTimeout(() => {
      setIsToggleHiding(false);
      setIsToggleVisible(true);
    }, 300);
  };

  const handleSectionChange = (section) => {
    setActiveSection(section);
    // If it's a section from a group, it will have an id but not a type
    const sectionId = section.id;
    if (sectionId) {
      navigate(`/docs/${sectionId}`);
    }
    if (window.innerWidth < 768) {
      handleSidebarClose();
    }
  };

  // Redirect to default section if no match found
  if (sectionId && !findSectionInStructure(sectionId)) {
    const defaultSection = getDefaultSection();
    return <Navigate to={`/docs/${defaultSection?.id}`} replace />;
  }

  return (
    <div className="docs-container d-flex min-vh-100">
      {/* Mobile Toggle Button */}
      {isToggleVisible && (
        <button
          className={`docs-sidebar-toggle d-md-none ${
            isToggleHiding ? 'hiding' : ''
          }`}
          onClick={handleToggleClick}
          aria-label="Toggle documentation menu"
        >
          <span className="toggle-icon">
            <i className="bi bi-book me-2"></i>
            {activeSection?.title || 'Documentation'}
            <i className="bi bi-chevron-right ms-2"></i>
          </span>
        </button>
      )}

      {/* Sidebar Overlay for Mobile */}
      {isSidebarOpen && (
        <div
          className="docs-sidebar-overlay d-md-none"
          onClick={handleSidebarClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`docs-sidebar-container ${
          isSidebarOpen ? 'show' : ''
        } bg-${theme}`}
      >
        <DocsSidebar
          structure={documentationStructure}
          activeSection={activeSection}
          onSectionChange={handleSectionChange}
          initialExpandedGroups={initialExpandedGroups}
        />
      </div>

      {/* Main Content */}
      <div className="docs-content-container bg-main">
        <DocsContent section={activeSection} />
      </div>
    </div>
  );
};

export default Docs;
